import React, { useContext, useMemo, useState } from 'react'
import {
  Workflow,
  WNode,
  WTransition,
  WManualTransition,
  WorkflowInfo,
  VariableOverrides,
  isVariableRef,
  Maybe,
} from './ow/model'
import { NodeChangedEvent } from './api/order-state'
import {
  Order,
  OrderWorkflowLog,
  OrderWorkflowEvent,
  OrderWorkflowEventParam,
  OrderWorkflowExtraEvent,
  OrderCtx,
} from './model-order'
import Graph, { D3NodeOptions, D3TransitionOptions } from './ow/graph-d3'
import * as R from 'ramda'
import { Panel, Line, Line2 } from './fragments/comp/panel'
import { Notification } from './notifications/model'
import NotificationInfo from './notifications/info'

import moment from 'moment'
import { AuditResource } from 'api/audit'
import SupportLogs from './support-logs'
import { ZupplerWorkflowEventLogs } from '../../../views/zuppler_logs'

const cx = require('classnames')

interface SupportProps {
  order: Order
  workflowInfo: WorkflowInfo
  workflow: Workflow
  currentWorkflowNode: NodeChangedEvent
  logs: OrderWorkflowLog[]
  events: OrderWorkflowEvent[]
  extraEvents: OrderWorkflowExtraEvent[]
  notifications: Notification[]
  /** restaurant's current variable overrides — "@name value" hints on the graph show these, falling back to defaults */
  overrides: VariableOverrides
}

type SelectionInfo = { type: 'none' } | { type: 'node'; node: WNode } | { type: 'transition'; transition: WTransition }

// ---- outcome classification ------------------------------------------------
// Why a transition fired: an action outcome (transport success/failure, business
// accept/reject), a timer, a scheduled moment, or a human/system manual act.
// Drives the edge colors — failures/rejections jump out when tracing an order.

interface TransitionVisit {
  position: number
  outcome: string
}

const transitionKey = (t: WTransition) =>
  `${t.from_node}>${t.to_node}>${t.transition_type}>${(t as WManualTransition).name || ''}`

/** What drove a logged transition: success/failure/accept/reject target of an
 * action on the from-node, the transition type itself for timed/scheduled, or
 * 'manual' when no action outcome references it (a person/system act). */
const outcomeOfLog = (log: OrderWorkflowLog, workflow: Workflow): string => {
  if (log.transition_type != 'manual') return log.transition_type
  const node = workflow.nodes.find((n) => n.id == log.from_node)
  if (node && log.transition) {
    for (const a of node.actions) {
      if (a.success == log.transition) return 'success'
      if (a.failure == log.transition) return 'failure'
      if (a.accept == log.transition) return 'accept'
      if (a.reject == log.transition) return 'reject'
    }
  }
  return 'manual'
}

// most problems first — a transition taken both successfully and later with a
// failure is colored by the failure
const OUTCOME_PRECEDENCE = ['manual', 'timer', 'scheduled', 'scheduled_date', 'scheduled_business_date', 'success', 'accept', 'reject', 'failure']

const worstOutcome = (visits: TransitionVisit[]): string =>
  visits.reduce((worst, v) => (OUTCOME_PRECEDENCE.indexOf(v.outcome) > OUTCOME_PRECEDENCE.indexOf(worst) ? v.outcome : worst), 'manual')

const OUTCOME_COLORS: { [outcome: string]: string } = {
  success: '#16a34a',
  failure: '#dc2626',
  reject: '#ea580c',
  accept: '#2563eb',
  manual: '#64748b',
  timer: '#d97706',
  scheduled: '#0ea5e9',
  scheduled_date: '#8b5cf6',
  scheduled_business_date: '#8b5cf6',
}

const outcomeBadgeClass = (outcome: string): string => {
  switch (outcome) {
    case 'success':
      return 'badge-success'
    case 'failure':
      return 'badge-danger'
    case 'reject':
      return 'badge-warning'
    case 'accept':
      return 'badge-info'
    case 'timer':
      return 'badge-warning'
    default:
      return 'badge-secondary'
  }
}

export default function Support(props: SupportProps) {
  // const [currentNode, setCurrentNode] = useState<WNode | null>(null)
  // const [currentTransition, setCurrentTransition] = useState<WTransition | null>(null)
  const [selection, setSelection] = useState<SelectionInfo>({ type: 'none' })
  const [graphSize, setGraphSize] = useState({ width: 600, height: 1000 })

  const currentNodeId = props.currentWorkflowNode.node

  // match a runtime log entry to its transition in the workflow content
  const logMatchesTransition = (log: OrderWorkflowLog, t: WTransition) =>
    log.from_node == t.from_node &&
    log.to_node == t.to_node &&
    log.transition_type == t.transition_type &&
    (!log.transition || log.transition == (t as WManualTransition).name)

  // ordered traversal per transition (1-based log order) with the outcome that fired it
  // — drives edge badges, tooltips and the outcome-based colors
  const transitionVisits = useMemo(() => {
    const visits = new Map<string, TransitionVisit[]>()
    props.logs.forEach((log, i) => {
      const t = props.workflow.transitions.find((t) => logMatchesTransition(log, t))
      if (!t) return
      const key = transitionKey(t)
      const arr = visits.get(key) || []
      arr.push({ position: i + 1, outcome: outcomeOfLog(log, props.workflow) })
      visits.set(key, arr)
    })
    return visits
  }, [props.logs, props.workflow.transitions])

  const edgeBadges = props.workflow.transitions.map((t) => {
    const v = transitionVisits.get(transitionKey(t))
    return v && v.length ? `#${v.map((visit) => visit.position).join(', #')}` : null
  })

  const getEdgeTooltip = (t: WTransition) => {
    const visits = transitionVisits.get(transitionKey(t)) || []
    const logs = props.logs.filter((log) => logMatchesTransition(log, t))
    if (!logs.length) return `${t.transition_type} — not taken`
    const byOutcome = R.countBy((visit) => visit.outcome, visits)
    const outcomeSummary = Object.entries(byOutcome)
      .map(([outcome, count]) => `${outcome}${count > 1 ? ` ×${count}` : ''}`)
      .join(', ')
    return (
      `${t.transition_type}${(t as WManualTransition).name ? ` "${(t as WManualTransition).name}"` : ''} — taken ${logs.length} time${logs.length > 1 ? 's' : ''} (${outcomeSummary})` +
      logs
        .map(
          (l, i) => `\n#${visits[i].position} ${visits[i].outcome} — ${moment(l.time).format('M/DD h:mm:ss a')} by ${l.initiator}`
        )
        .join('')
    )
  }

  const getNodeTooltip = (n: WNode) => {
    const visits = props.logs.filter((log) => log.from_node == n.id || log.to_node == n.id)
    const lines = [`${n.name} [${n.id}]${visits.length ? ' — visited' : ''}`]
    if (n.actions.length) {
      lines.push('')
      n.actions.forEach((a) => {
        lines.push(`${a.name}${a.params && a.params.template ? ` (${a.params.template})` : ''}`)
        if (a.success) lines.push(`  on success: ${a.success}`)
        if (a.failure) lines.push(`  on failure: ${a.failure}`)
        if (a.accept) lines.push(`  on accept: ${a.accept}`)
        if (a.reject) lines.push(`  on reject: ${a.reject}`)
      })
    }
    return lines.join('\n')
  }

  const getNodeOptions = (n: WNode): D3NodeOptions => ({
    shape: currentNodeId == n.id ? 'ellipse' : 'rect',
    class: cx({
      visited: !!props.logs.find((log) => log.from_node == n.id || log.to_node == n.id),
      current: n.id == props.currentWorkflowNode.node,
      selected: selection.type == 'node' && selection.node.id == n.id,
    }),
  })

  const getTransitionOptions = (t: WTransition): D3TransitionOptions => {
    const visits = transitionVisits.get(transitionKey(t)) || []
    const outcome = worstOutcome(visits)
    return {
      class: cx(t.transition_type, `outcome-${outcome}`, {
        visited: visits.length > 0,
        selected:
          selection.type == 'transition' &&
          selection.transition.from_node == t.from_node &&
          selection.transition.to_node == t.to_node &&
          selection.transition.transition_type == t.transition_type,
      }),
      arrowheadStyle: visits.length > 0 ? `fill: ${OUTCOME_COLORS[outcome] || '#16a34a'}` : undefined,
    }
  }

  const onSelectNode = (n: WNode) => {
    // setCurrentNode(n)
    setSelection({ type: 'node', node: n })
    console.log('Node selected', n, props.events)
  }

  const onSelectTransition = (t: WTransition) => {
    const findLogByTransition = (log: OrderWorkflowLog) =>
      log.transition == (t as WManualTransition).name &&
      log.transition_type == t.transition_type &&
      log.from_node == t.from_node &&
      log.to_node == t.to_node

    const logs = props.logs.filter(findLogByTransition)
    // setCurrentTransition(t)
    setSelection({ type: 'transition', transition: t })
  }

  const currentNode = selection.type == 'node' ? selection.node : null
  const currentTransition = selection.type == 'transition' ? selection.transition : null

  return (
    <div className="row">
      <div className="col-7">
        <Panel
          id="workflow"
          label={`Order Workflow: ${props.workflowInfo.name}`}
          fullHeight={true}
          onResize={setGraphSize}>
          <OwGraphLegend />
          <Graph
            orderId={props.order.uuid}
            workflow={props.workflow}
            currentNode={currentNodeId}
            onSelectNode={onSelectNode}
            onSelectTransition={onSelectTransition}
            getNodeOptions={getNodeOptions}
            getTransitionOptions={getTransitionOptions}
            width={graphSize.width}
            height={graphSize.height}
            externalActions={[]}
            overrides={props.overrides}
            getNodeTooltip={getNodeTooltip}
            getEdgeTooltip={getEdgeTooltip}
            edgeBadges={edgeBadges}
          />
        </Panel>
      </div>
      <div className="col-5">
        <AuditResource resourceType={'order'} resourceId={props.order.uuid}>
          <SupportLogs orderId={props.order.uuid} />
        </AuditResource>
        <WorkflowLogs
          events={props.events}
          logs={props.logs}
          workflow={props.workflow}
          currentNode={currentNode}
          onSetCurrentNode={onSelectNode}
          onSelectTransition={onSelectTransition}
          currentTransition={currentTransition}
        />
        <WorkflowEvents
          events={props.events}
          logs={props.logs}
          workflow={props.workflow}
          currentNode={currentNode}
          onSetCurrentNode={onSelectNode}
          currentTransition={currentTransition}
          overrides={props.overrides}
        />
        <WorkflowExtraEvents
          extraEvents={props.extraEvents}
          events={props.events}
          logs={props.logs}
          workflow={props.workflow}
          currentNode={currentNode}
          onSetCurrentNode={onSelectNode}
          currentTransition={currentTransition}
        />
        <NotificationsList
          currentNode={currentNode}
          currentTransition={currentTransition}
          workflow={props.workflow}
          onSetCurrentNode={onSelectNode}
          notifications={props.notifications}
          order={props.order}
        />
      </div>
    </div>
  )
}

function NotificationsList(props: {
  notifications: Notification[]
  workflow: Workflow
  currentNode: WNode | null
  currentTransition: WTransition | null
  onSetCurrentNode: (n: WNode | null) => void
  order: Order
}) {
  const notifications = props.notifications //props.currentNode ?
  // props.notifications.filter((n: Notification) => n.workflow_nodes.includes(props.currentNode.id)) : props.notifications

  return (
    <Panel id="current-node" label="Notifications">
      {notifications.map((n: Notification) => (
        <NotificationInfo
          key={n.id}
          notification={n}
          highlight={!!props.currentNode && n.workflow_nodes.includes(props.currentNode.id)}
          onSetCurrentNode={props.onSetCurrentNode}
          workflow={props.workflow}
          order={props.order}
        />
      ))}
    </Panel>
  )
}

interface SelectionInfoProps extends SupportProps {
  currentNode: WNode | null
}
function OwGraphLegend(): JSX.Element {
  return (
    <div className="ow-graph-legend">
      <span className="legend-item">
        <span className="legend-node current" /> current node
      </span>
      <span className="legend-item">
        <span className="legend-node" /> visited node
      </span>
      <span className="legend-item">
        <span className="legend-swatch" /> success
      </span>
      <span className="legend-item">
        <span className="legend-swatch failure" /> failure
      </span>
      <span className="legend-item">
        <span className="legend-swatch reject" /> rejected
      </span>
      <span className="legend-item">
        <span className="legend-swatch accept" /> accepted
      </span>
      <span className="legend-item">
        <span className="legend-swatch human" /> human/system
      </span>
      <span className="legend-item">
        <span className="legend-swatch timer" /> timer
      </span>
      <span className="legend-item">
        <span className="legend-swatch scheduled" /> scheduled
      </span>
    </div>
  )
}

function SelectionInfo(props: SelectionInfoProps) {
  const currentNodeNotifications = props.notifications.filter(
    (n: Notification) => props.currentNode && n.workflow_nodes.includes(props.currentNode.id)
  )

  return (
    <Panel id="current-node" label="Selection Info">
      {props.currentNode && <Line label="Current Node">{props.currentNode.name}</Line>}
      {props.currentNode && props.currentNode.actions.length > 0 && (
        <Line2 key="order_actions">
          <h5>Workflow Actions</h5>
          <ul>
            {props.currentNode.actions.map((action, index) => (
              <li key={index}>
                {action.name}
                {action.notes && (
                  <>
                    <br />
                    {action.notes}
                  </>
                )}
                <ul>
                  {action.success && <li key="1">on success: {action.success}</li>}
                  {action.failure && <li key="2">on failure: {action.failure}</li>}
                </ul>
              </li>
            ))}
          </ul>
        </Line2>
      )}
    </Panel>
  )
}

function WorkflowLogs(props: {
  events: OrderWorkflowEvent[]
  logs: OrderWorkflowLog[]
  workflow: Workflow
  currentNode: WNode | null
  currentTransition: WTransition | null
  onSetCurrentNode: (n: WNode | null) => void
  onSelectTransition: (t: WTransition) => void
}) {
  const logs = props.logs // props.currentNode ?
  // props.logs.filter((log: OrderWorkflowLog) => log.from_node == props.currentNode.id) : props.logs
  return (
    <Panel id="logs" label="Workflow History">
      {logs.map((log, index) => (
        <LogLine
          key={index}
          log={log}
          workflow={props.workflow}
          onSelectTransition={props.onSelectTransition}
          highlight={
            !!props.currentTransition &&
            props.currentTransition.from_node == log.from_node &&
            props.currentTransition.to_node == log.to_node &&
            (props.currentTransition as WManualTransition).name == log.transition
          }
          events={props.events.filter((event) => [log.from_node].includes(event.node_when_triggered))}
          startNode={R.find(R.propEq('id', log.from_node), props.workflow.nodes) as WNode}
          endNode={R.find(R.propEq('id', log.to_node), props.workflow.nodes) as WNode}
          onSetCurrentNode={props.onSetCurrentNode}
        />
      ))}
    </Panel>
  )
}

function LogLine({
  log,
  events,
  startNode,
  endNode,
  onSetCurrentNode,
  onSelectTransition,
  workflow,
  highlight,
}: {
  log: OrderWorkflowLog
  events: OrderWorkflowEvent[]
  startNode: WNode | null
  endNode: WNode | null
  onSetCurrentNode: (n: WNode) => void
  onSelectTransition: (t: WTransition) => void
  workflow: Workflow
  highlight: boolean
}) {
  const [expand, setExpand] = useState(false)
  // clicking a history line highlights the matching transition on the graph
  const selectTransition = () => {
    const t = workflow.transitions.find(
      (t) =>
        log.from_node == t.from_node &&
        log.to_node == t.to_node &&
        log.transition_type == t.transition_type &&
        (!log.transition || log.transition == (t as WManualTransition).name)
    )
    if (t) onSelectTransition(t)
  }
  return (
    <Line2 highlight={highlight} onClick={selectTransition}>
      <div>
        <span>
          <span
            className={cx('fa', { 'fa-plus': !expand, 'fa-minus': expand })}
            onClick={() => setExpand(!expand)}></span>{' '}
          <span className="acts-as-link" onClick={() => startNode && onSetCurrentNode(startNode)}>
            {startNode ? <>{startNode.name}</> : <span className="label label-danger">{log.from_node}</span>}
          </span>{' '}
          -&gt; {log.initiator} {log.transition} -&gt;{' '}
          <span className="acts-as-link" onClick={() => endNode && onSetCurrentNode(endNode)}>
            {endNode ? <>{endNode.name}</> : <span className="label label-danger">{log.to_node}</span>}
          </span>{' '}
          <span className={`badge ${outcomeBadgeClass(outcomeOfLog(log, workflow))}`}>
            {log.transition_type}
            {outcomeOfLog(log, workflow) != 'manual' && outcomeOfLog(log, workflow) != log.transition_type
              ? ` (${outcomeOfLog(log, workflow)})`
              : ''}
          </span>
        </span>
      </div>
      {(highlight || expand) && (
        <div>
          <div key="timestamp">
            <em>Time:</em> {moment(log.time).format('M/DD h:mm:ss a Z')}
          </div>
          {log.notifications_triggered + log.orderActions_triggered > 0 && (
            <div key="triggers">
              <em>Triggered:</em> {log.notifications_triggered} notifications &amp; {log.orderActions_triggered} actions
            </div>
          )}
          {false && (
            <ul>
              {events.map((event) => (
                <li>
                  <div>
                    {eventTypeAndParams(event.type, event.params)} {event.sender}: {event.outcome_event}
                  </div>
                  <div>
                    Start Time: {moment(event.start_time).format('M/DD h:mm:ss a Z')} for {event.time}s
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Line2>
  )
}

function WorkflowEvents(props: {
  events: OrderWorkflowEvent[]
  logs: OrderWorkflowLog[]
  workflow: Workflow
  currentNode: WNode | null
  currentTransition: WTransition | null
  onSetCurrentNode: (n: WNode | null) => void
  overrides: VariableOverrides
}) {
  const events = props.events //props.currentNode ? props.events.filter((event) => event.node_when_triggered == props.currentNode.id ) : props.events
  return (
    <Panel id="logs" label="Notification & Order Action Logs">
      {events.map((event, index) => (
        <ActionLine
          key={index}
          event={event}
          highlight={
            (!!props.currentNode && event.node_when_triggered == props.currentNode.id) ||
            (!!props.currentTransition && props.currentTransition.from_node == event.node_when_triggered)
          }
          triggeredNode={R.find(R.propEq('id', event.node_when_triggered), props.workflow.nodes) as WNode}
          receivedNode={R.find(R.propEq('id', event.node_when_received), props.workflow.nodes) as WNode}
          outcomeEvent={event.outcome_event}
          outcomeMessage={event.outcome_message}
          onSetCurrentNode={props.onSetCurrentNode}
          overrides={props.overrides}
        />
      ))}
    </Panel>
  )
}

function eventTypeAndParams(type: string, params: OrderWorkflowEventParam | null, overrides: VariableOverrides = {}) {
  if (!params) return type
  switch (type) {
    case 'Mark':
      return `${type} as ${params.state}`
    case 'SubscribeRDS':
    case 'SubscribePayment':
      return `${type} for ${params.event}`
    case 'NotifyPOS':
    case 'NotifyLoyalty':
      return `${type} say ${params.verb}`
    case 'RestaurantServiceEmail':
    case 'RestaurantServiceSMS':
    case 'RestaurantServiceFax':
    case 'ChannelEmail':
    case 'ChannelSMS':
    case 'UserEmail':
    case 'UserSMS': {
      // event params can hold a variable lookup when the runtime stores the raw
      // content value — hint the variable and show the current override, else the default
      const template = (params as any).template
      if (isVariableRef(template)) {
        const override = overrides[template.variable]
        const value = override !== undefined && override !== null ? override : template.default
        return `${type} using template @${template.variable} ${value}`
      }
      return `${type} using template ${template}`
    }
    default:
      return type
  }
}

function outcomeToBadge(outcome: 'success' | 'failure' | 'accept' | 'reject'): string {
  switch (outcome) {
    case 'success':
      return 'success'
    case 'failure':
      return 'danger'
    case 'accept':
      return 'info'
    case 'reject':
      return 'warnning'
    default:
      return ''
  }
}

function ActionLine(props: {
  event: OrderWorkflowEvent
  triggeredNode: WNode | null
  receivedNode: WNode | null
  highlight: boolean
  outcomeEvent: string
  outcomeMessage: string
  onSetCurrentNode: (n: WNode) => void
  overrides: VariableOverrides
}) {
  const [expand, setExpand] = useState(false)
  const order = useContext(OrderCtx)
  return (
    <Line2 highlight={props.highlight}>
      <div>
        <span>
          <span className={`badge badge-${outcomeToBadge(props.event.outcome_event)}`}>{props.event.sender}</span>{' '}
          <span className="acts-as-link" onClick={() => setExpand(!expand)}>
            {eventTypeAndParams(props.event.type, props.event.params, props.overrides)}
          </span>{' '}
          @{' '}
          <div key="timestamp">
            <em>Time:</em> {moment(props.event.start_time).format('M/DD h:mm:ss a Z')} for {props.event.time}s
          </div>
        </span>
      </div>
      {(expand || props.highlight) && (
        <>
          <div>
            <span
              className="acts-as-link"
              onClick={() => props.triggeredNode && props.onSetCurrentNode(props.triggeredNode)}>
              {props.triggeredNode ? (
                <>{props.triggeredNode.name}</>
              ) : (
                <span className="label label-danger">{props.event.node_when_triggered}</span>
              )}
            </span>{' '}
            -&gt; <em>{props.outcomeEvent}</em> -&gt;{' '}
            <span
              className="acts-as-link"
              onClick={() => props.receivedNode && props.onSetCurrentNode(props.receivedNode)}>
              {props.receivedNode ? (
                <>{props.receivedNode.name}</>
              ) : (
                <span className="label label-danger">{props.event.node_when_received}</span>
              )}
            </span>
          </div>
          {props.outcomeMessage && (
            <div>
              Response: <strong>{props.outcomeMessage}</strong>
            </div>
          )}
        </>
      )}
      <ZupplerWorkflowEventLogs
        sessionId={order?.uuid as string}
        event={props.event}
        ownerId={String(order?.carts.restaurant_id)}
      />
    </Line2>
  )
}

function WorkflowExtraEvents(props: {
  extraEvents: OrderWorkflowExtraEvent[]
  events: OrderWorkflowEvent[]
  logs: OrderWorkflowLog[]
  workflow: Workflow
  currentNode: WNode | null
  currentTransition: WTransition | null
  onSetCurrentNode: (n: WNode | null) => void
}) {
  return props.extraEvents.length > 0 ? (
    <Panel id="logs" label="Workflow Events">
      {props.extraEvents.map((ee, index) => (
        <ExtraEventActionLine
          key={index}
          entry={ee}
          node={props.workflow.nodes.find((n) => n.id == ee.node_when_received)}
        />
      ))}
    </Panel>
  ) : (
    <></>
  )
}

function ExtraEventActionLine(props: { entry: OrderWorkflowExtraEvent; node?: WNode }): JSX.Element {
  const {
    initiator,
    created_at,
    name,
    node_when_received,
    notifications_triggered,
    order_actions_triggered,
  } = props.entry
  return (
    <Line2>
      <div>
        <span>
          <span className="label label-info">{name.toLocaleUpperCase()}</span> by {initiator} @{' '}
          <span className={'badge badge-info'}>{props.node ? props.node.name : node_when_received}</span>
          <div key="timestamp">
            <em>Time:</em> {moment(created_at).format('M/DD h:mm:ss a Z')}
          </div>
          {notifications_triggered + order_actions_triggered > 0 && (
            <div key="triggers">
              <em>Triggered:</em> {notifications_triggered} notifications &amp; {order_actions_triggered} actions
            </div>
          )}
        </span>
      </div>
    </Line2>
  )
}
